import { removeNumberPropFromInputs } from '../utils/formatting'
import { FormSubmissionError, getFormSubmissionErrorMessage } from '../custom-errors'
import type { GuestCorvid, RsvpCorvid, RsvpStatus } from '../types/types'
import type {
	AdditionalGuestDetails,
	CreateRsvpRequest,
	CreateRsvpResponse,
	FormResponse,
	Guest as V2Guest,
	Rsvp as V2Rsvp,
} from '@wix/ambassador-events-v2-rsvp/types'
// eslint-disable-next-line eslint/no-restricted-imports
import type { AmbassadorHTTPError } from '@wix/ambassador/dist/src/runtime/http'
import { EventsApi } from '../utils/api'
import { toCorvidRsvpStatus, toV2RsvpStatus, V2_TO_FORM_SUBMISSION_ERROR } from '../rsvp-status'

const getFormInputValue = (inputValues: FormResponse['inputValues'], inputName: string) =>
	inputValues?.find((input) => input.inputName === inputName)?.value

const getFormInputValues = (inputValues: FormResponse['inputValues'], inputName: string) =>
	inputValues?.find((input) => input.inputName === inputName)?.values

const buildAdditionalGuestDetails = (inputValues: FormResponse['inputValues']): AdditionalGuestDetails | undefined => {
	const guestCountValue = getFormInputValue(inputValues, 'additionalGuests')
	const guestNames = getFormInputValues(inputValues, 'guestNames') ?? []

	if (guestCountValue === undefined && !guestNames.length) {
		return undefined
	}

	return {
		guestCount: guestCountValue !== undefined ? Number(guestCountValue) : undefined,
		guestNames: guestNames.length ? guestNames : undefined,
	}
}

export const buildCreateRsvpRequest = (eventId: string, form: FormResponse, status: RsvpStatus): CreateRsvpRequest => {
	const additionalGuestDetails = buildAdditionalGuestDetails(form.inputValues)

	return {
		rsvp: {
			eventId,
			status: toV2RsvpStatus(status),
			firstName: getFormInputValue(form.inputValues, 'firstName'),
			lastName: getFormInputValue(form.inputValues, 'lastName'),
			email: getFormInputValue(form.inputValues, 'email'),
			...(additionalGuestDetails && { additionalGuestDetails }),
			form,
		},
	}
}

const mapGuestToCorvid = (guest: V2Guest): GuestCorvid => ({
	index: guest.index ?? undefined,
	fullName: guest.fullName ?? undefined,
	id: guest.guestId ?? undefined,
	checkIn: guest.checkInDetails
		? {
				created: guest.checkInDetails.checkInDate,
				checkInCanceledDate: guest.checkInDetails.checkInCanceledDate,
				checkedInBy: guest.checkInDetails.checkedInBy,
				checkInCanceledBy: guest.checkInDetails.checkInCanceledBy,
				checkedInByStaffMember: guest.checkInDetails.checkedInByStaffMember,
				checkInCanceledByStaffMember: guest.checkInDetails.checkInCanceledByStaffMember,
			}
		: undefined,
})

export const mapRsvpToCorvid = (rsvp: V2Rsvp): RsvpCorvid => ({
	id: rsvp.id ?? undefined,
	eventId: rsvp.eventId ?? undefined,
	contactId: rsvp.contactId ?? undefined,
	memberId: rsvp.memberId ?? undefined,
	firstName: rsvp.firstName ?? undefined,
	lastName: rsvp.lastName ?? undefined,
	email: rsvp.email ?? undefined,
	status: toCorvidRsvpStatus(rsvp.status),
	totalGuests: rsvp.totalGuests ?? undefined,
	guests: rsvp.guestList?.guests?.map(mapGuestToCorvid),
	anonymized: rsvp.anonymized ?? undefined,
	language: rsvp.language,
	locale: rsvp.locale,
	marketingConsent: rsvp.marketingConsent,
	createdDate: rsvp.createdDate ? new Date(rsvp.createdDate) : new Date(),
	updatedDate: rsvp.updatedDate ? new Date(rsvp.updatedDate) : new Date(),
	rsvpForm: removeNumberPropFromInputs(rsvp.form ?? {}),
})

export const createRsvp = async (eventId: string, form: FormResponse, status: RsvpStatus): Promise<RsvpCorvid> => {
	const request = buildCreateRsvpRequest(eventId, form, status)

	return (await EventsApi())
		.RsvpManagement()
		.createRsvp(request)
		.then((response) => handleCreateRsvpSuccess(response))
		.catch((err) => handleCreateRsvpError(err))
}

export const handleCreateRsvpSuccess = (response: CreateRsvpResponse): RsvpCorvid => {
	const { rsvp } = response
	if (!rsvp) {
		throw new Error('Missing RSVP in create response')
	}

	return mapRsvpToCorvid(rsvp)
}

export const handleCreateRsvpError = (error: AmbassadorHTTPError) => {
	const applicationError = error?.response?.details?.applicationError
	if (applicationError?.code) {
		const errorKey = V2_TO_FORM_SUBMISSION_ERROR[applicationError.code]
		if (errorKey) {
			const maxRsvpSize = applicationError.data?.maxRsvpSize ?? 0
			const errorMessage =
				getFormSubmissionErrorMessage(errorKey, { max_rsvp_size: maxRsvpSize }) ||
				applicationError.description ||
				error.response.message
			throw new FormSubmissionError(errorMessage, errorKey)
		}
	}

	throw error
}
